import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Donation, DonationsByAccountResponse } from './DonationType';
import DonationApi from './DonationApi';
import AccountApi from '../account/accountApi';
import { formatTimestamp } from '../lib/global_function';
import { validateEmail, validateAndFormatPhoneNumber } from '../lib/validator';
import { getAccountIdFromSessionStorage } from '../lib/global_function';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Spinner from '../components/ui/spinner';
import { 
  FileText, 
  Send, 
  Heart, 
  Plus,
  RefreshCw,
  DollarSign,
  Calendar,
  Hash,
  TrendingUp,
  Mail,
  MessageSquare
} from 'lucide-react';

const breadcrumbs = [
  { label: 'Account', path: '/main/account' },
  { label: 'My Donations', path: '' },
];

const Donations: React.FC = () => {
  const navigate = useNavigate();
  
  const [donations, setDonations] = useState<Donation[]>([]);
  const [summary, setSummary] = useState<{ total_donations: number; total_amount: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [sendingStatement, setSendingStatement] = useState(false);

  // Send statement inline state
  const [sendMethod, setSendMethod] = useState<'email' | 'text' | null>(null);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [statementSent, setStatementSent] = useState(false);

  useEffect(() => {
    const fetchAccountData = async () => {
      const id = getAccountIdFromSessionStorage(process.env.REACT_APP_SESSION_STORAGE_KEY as string);
      setAccountId(id);
      
      if (id) {
        try {
          const account = await AccountApi.getAccount(id);
          if (account?.email) setEmail(account.email);
          if (account?.phone) setPhone(account.phone);
        } catch (error) {
          console.error('Failed to fetch account data:', error);
        }
      }
    };
    
    fetchAccountData();
  }, []);

  useEffect(() => {
    if (accountId) {
      fetchDonations();
    }
  }, [accountId]);

  const fetchDonations = async () => {
    if (!accountId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response: DonationsByAccountResponse = await DonationApi.getDonationsByAccountId(accountId);
      setDonations(response.donations);
      setSummary(response.summary);
    } catch (err) {
      if (err instanceof Error && err.message.includes('No donations found')) {
        setDonations([]);
        setSummary({ total_donations: 0, total_amount: 0 });
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load donations.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewPdf = async (donationId?: string) => {
    if (!accountId) return;

    try {
      const blob = await DonationApi.viewDonationStatementPdf(accountId, donationId);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to view PDF.');
    }
  };

  const handleSendMethodChange = (method: 'email' | 'text') => {
    setSendMethod(method);
    setStatementSent(false);
    setSuccessMessage(null);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let errors: string[] = [];
    setError(null);
    
    switch (name) {
      case 'phone': {
        const phoneResult = validateAndFormatPhoneNumber(value);
        if (!phoneResult.isValid) {
          errors.push(phoneResult.error || 'Invalid phone number');
        } else {
          setPhone(phoneResult.value);
        }
        break;
      }
      case 'email': {
        const emailResult = validateEmail(value);
        if (!emailResult.isValid) {
          errors.push(emailResult.error || 'Invalid email address format');
        } else {
          setEmail(emailResult.value);
        }
        break;
      }
    }
    
    if (errors.length > 0) {
      setError(errors.join(', '));
    }
  };

  const handleSendStatement = async () => {
    if (!accountId) {
      setError('Account not found.');
      return;
    }

    if (!sendMethod) {
      setError('Please select a send method (Email or Text).');
      return;
    }

    // Validate and format based on send method
    let validatedEmail = email;
    let validatedPhone = phone;

    switch (sendMethod) {
      case 'email': {
        if (!email) {
          setError('Please enter an email address.');
          return;
        }
        
        const emailResult = validateEmail(email);
        if (!emailResult.isValid) {
          setError(emailResult.error || 'Invalid email address format');
          return;
        }
        validatedEmail = emailResult.value;
        break;
      }
      case 'text': {
        if (!phone) {
          setError('Please enter a phone number.');
          return;
        }
        
        const phoneResult = validateAndFormatPhoneNumber(phone);
        if (!phoneResult.isValid) {
          setError(phoneResult.error || 'Invalid phone number');
          return;
        }
        validatedPhone = phoneResult.value;
        break;
      }
    }

    setSendingStatement(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await DonationApi.sendDonationStatement({
        account_id: accountId,
        donation_id: undefined,
        recipient_email: validatedEmail || '',
        recipient_phone: validatedPhone || undefined,
        send_method: sendMethod === 'text' ? 'sms' : 'email',
      });
      
      setSuccessMessage(
        sendMethod === 'email' 
          ? 'Donation statement has been sent to your email!' 
          : 'Donation statement has been sent to your phone!'
      );
      setStatementSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send statement.');
    } finally {
      setSendingStatement(false);
    }
  };

  const handleNewDonation = () => {
    navigate('/main/account/donation/create');
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mt-6 mb-6">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold text-foreground mb-2 flex items-center gap-2">
              <Heart className="size-7 text-primary" />    
              My Donations        
            </h2>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              size="medium"
              variant="inverted"
              onClick={() => handleViewPdf()}
            >
              <FileText size={16} />
              View in PDF
            </Button>

            <Button
              size="medium"
              variant="default"
              onClick={handleNewDonation}
            >
              <Plus size={16} />
              Make a Donation
            </Button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <Alert variant="error" className="mb-6">
            {error}
          </Alert>
        )}

        {/* Success Message */}
        {successMessage && (
          <Alert variant="success" className="mb-6">
            {successMessage}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Spinner />
        ) : (
          <>
            {/* Summary Cards */}
            {summary && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Hash className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Total Donations</div>
                      <div className="text-xl font-bold text-foreground">{summary.total_donations}</div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Total Amount</div>
                      <div className="text-xl font-bold text-green-600">
                        ${Number(summary.total_amount).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Average Donation</div>
                      <div className="text-xl font-bold text-purple-600">
                        ${summary.total_donations > 0 
                          ? (summary.total_amount / summary.total_donations).toFixed(2) 
                          : '0.00'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Heart className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Supporter Status</div>
                      <div className="text-lg font-bold text-primary">
                        {summary.total_amount >= 100 ? '🌟 Champion' : 
                         summary.total_amount >= 50 ? '⭐ Star' : 
                         summary.total_amount > 0 ? '💖 Supporter' : 'New'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Send Statement Section - Hidden after successful send */}
            {donations.length > 0 && !statementSent && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
                <h3 className="text-lg font-semibold text-foreground mb-4">Send Donation Statement</h3>

                <div className="space-y-5">
                  <div className="flex gap-6 mt-5">
                    <label className="flex items-center gap-2 text-base font-medium text-foreground cursor-pointer">
                      <input
                        type="radio"
                        value="email"
                        checked={sendMethod === 'email'}
                        onChange={() => handleSendMethodChange('email')}
                        className="size-4 cursor-pointer"
                      />
                      <Mail className="size-5" />
                      Email
                    </label>

                    <label className="flex items-center gap-2 text-base font-medium text-foreground cursor-pointer">
                      <input
                        type="radio"
                        value="text"
                        checked={sendMethod === 'text'}
                        onChange={() => handleSendMethodChange('text')}
                        className="size-4 cursor-pointer"
                      />
                      <MessageSquare className="size-5" />
                      Text
                    </label>
                  </div>

                  {sendMethod === 'email' && (
                    <div>
                      <Label htmlFor="email" className="text-base">
                        Recipient Email Address: <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter email address"
                        onBlur={handleBlur}
                        required
                        className="mt-2"
                      />
                    </div>
                  )}

                  {sendMethod === 'text' && (
                    <div>
                      <Label htmlFor="phone" className="text-base">
                        Recipient Phone Number: <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        name="phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Enter phone number"
                        onBlur={handleBlur}
                        required
                        className="mt-2"
                      />
                    </div>
                  )}

                  <Button
                    size="medium"
                    variant="default"
                    onClick={handleSendStatement}
                    disabled={sendingStatement}
                  >
                    {sendingStatement ? (
                      <>
                        <Spinner size="sm" fullScreen={false} className="size-4" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        Send Statement
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Donations Table - Desktop */}
            {donations.length > 0 ? (
              <>
                <div className="hidden md:block bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-100 border-b">
                          <th className="px-4 py-3 text-left font-semibold text-foreground">#</th>
                          <th className="px-4 py-3 text-left font-semibold text-foreground">Date</th>
                          <th className="px-4 py-3 text-left font-semibold text-foreground">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {donations.map((donation, index) => (
                          <tr 
                            key={donation.donation_id} 
                            className="hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-4 py-3 text-gray-600">{index + 1}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <Calendar size={14} className="text-gray-400" />
                                {formatTimestamp(donation.created_at || '')}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="font-bold text-green-600 text-base">
                                ${Number(donation.amount).toFixed(2)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-green-50 border-t-2 border-gray-300">
                          <td className="px-4 py-3 font-bold" colSpan={2}>Total:</td>
                          <td className="px-4 py-3 font-bold text-green-600 text-lg">
                            ${donations.reduce((sum, d) => sum + Number(d.amount), 0).toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Donations Cards - Mobile */}
                <div className="md:hidden space-y-3">
                  {donations.map((donation, index) => (
                    <div 
                      key={donation.donation_id} 
                      className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <span className="font-bold text-green-600 text-xl">
                          ${Number(donation.amount).toFixed(2)}
                        </span>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          #{index + 1}
                        </span>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date:</span>
                          <span className="font-medium">{formatTimestamp(donation.created_at || '')}</span>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Mobile Total */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-foreground">Total Donated:</span>
                      <span className="font-bold text-green-600 text-xl">
                        ${donations.reduce((sum, d) => sum + Number(d.amount), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* Empty State */
              <div className="text-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-200 rounded-full mb-4">
                  <Heart className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No Donations Yet</h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  You haven't made any donations yet. Your support helps us improve our services and build new features! 
                </p>
                <Button
                  size="medium"
                  variant="default"
                  onClick={handleNewDonation}
                >
                  <Heart size={16} />
                  Make Your First Donation
                </Button>
              </div>
            )}

            {/* Thank You Message */}
            {donations.length > 0 && (
              <div className="mt-8 text-center p-6 bg-orange-50 rounded-lg border border-primary/30">
                <Heart className="w-8 h-8 text-primary mx-auto mb-2" fill="currentColor" />
                <h3 className="text-lg font-semibold text-gray-800 mb-1">
                  Thank You for Your Support!
                </h3>
                <p className="text-gray-600 text-sm">
                  Your donations help us continue improving MrJoe Pro for contractors everywhere.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Donations;